import Cookies, { type CookieAttributes } from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../common/utils';
import { isProcessingAllowedByPreferences } from '../../../common/utils/item-processing';
import { Logger } from '../../../common/utils/logger';
import { CategoryStorageType, type PreferenceCategory, Status } from '../../../types';
export type { CookieAttributes as JSCookieAttributes };

/**
 * Sets a cookie asynchronously. This queries user preferences before setting
 * the cookie, so it must be used for any cookies which are not strictly
 * necessary.
 *
 * @param attributes - Forwarded as-is to js-cookie's `Cookies.set`. The `CookieAttributes` type is
 *   broader than what js-cookie actually serializes: its index signature accepts arbitrary
 *   keys/values, but js-cookie only handles `expires` (`number | Date`), string-valued attributes
 *   (`path`, `domain`, …) and boolean flags (`secure`). A non-string value under any other key
 *   makes js-cookie throw while serializing. This function does not coerce attribute values.
 *
 * @throws Behind `platform_moonjelly_bsc_throw_cookie_errors`, re-throws any error from
 *   `Cookies.set` (e.g. an invalid attribute value) after recording a `usedSetCookieError`
 *   operational event, so callers passing invalid attributes fail fast. When the gate is off the
 *   error is logged and swallowed and `Status.BLOCKED` is returned (the previous behaviour).
 */
export const setCookie = async (
	key: string,
	value: string,
	attributes?: CookieAttributes,
): Promise<string> => {
	if (fg('platform_moonjelly_bsc_master_killswitch')) {
		// Set the cookie without checking user preferences when master killswitch is enabled.
		// Emit analytics so we can quantify how often the killswitch path is taken per product
		if (fg('platform_bsc_consent_bypass_analytics')) {
			sendPackageOperationalEvent({
				action: 'usedSetCookieMasterKillswitch',
				attributes: {
					cookieKey: key,
					product: getBscGlobalState().product,
				},
			});
		}
		Cookies.set(key, value, { ...attributes, 'atl-set-cookie': true });
		return Status.SUCCESS;
	}

	// user belongs to an org that opted into cookies management and we should respect their preferences

	const productName = getBscGlobalState().product;
	const wasInitialized = getBscGlobalState().hasInitialized;

	// Flag enabled, user belongs to an org that opted into cookies management and we should respect their preferences
	let blockedCookieItemCategory: PreferenceCategory | null | undefined;
	const shouldSetCookie = await isProcessingAllowedByPreferences({
		itemKey: key,
		storageType: CategoryStorageType.Cookies,
		productName,
		blockedCallback: ({ itemCategory }) => {
			blockedCookieItemCategory = itemCategory;
		},
	});

	const eventAttributes = {
		wasRejected: !shouldSetCookie,
		cookieKey: key,
	};
	try {
		sendPackageOperationalEvent({
			action: 'usedSetCookie',
			attributes: eventAttributes,
		});

		if (!wasInitialized) {
			sendPackageOperationalEvent({
				action: 'setItemWithoutInit',
				attributes: { itemKey: key, storageType: 'Cookie' },
			});
		}

		if (shouldSetCookie) {
			Cookies.set(key, value, { ...attributes, 'atl-set-cookie': true });
			return Status.SUCCESS;
		} else {
			if (blockedCookieItemCategory === null) {
				Logger.warn(
					`setCookie('${key}', ...) was blocked` +
						`${productName ? ` for product "${productName}"` : ''}` +
						`: '${key}' is not registered in the BSC data store. Add it to the data store before calling this API.`,
				);
			}
			return Status.BLOCKED;
		}
	} catch (e: any) {
		Logger.errorWithOperationalEvent({
			action: 'usedSetCookieError',
			attributes: eventAttributes,
			message: `Failed to use set cookie. ${e.message || ''}`,
		});
		if (fg('platform_moonjelly_bsc_throw_cookie_errors')) {
			throw e;
		}
		return Status.BLOCKED;
	}
};
